import { Dot, Star } from "lucide-react";

import { ProgressBar } from "@components/items/display/ShadcnProgressBar";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { Badge } from "@ui/badge";
import { SVG } from "@components/items/common/SVG";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import { convertMsToHourMinSec } from "@utils/helpers/timeFormtter";
import useCurrentPlan from "@utils/hooks/useCurrentPlan";
import TranslatedText from "@components/items/display/TranslatedText";
import { setIsSubscriptionModalOpen } from "@lib/stores";

import BookMenu from "./BookMenu";
import MarkdownView from "react-showdown";
import { formatRecommended } from "@utils/helpers/textFormatter";

export interface ContentDetailsParameters {
  aboutHubTitle: string | undefined;
  authorsTitle: string | undefined;
  recommendedToTitle: string | undefined;
  recommendedTo: string | undefined;
  descriptionTitle: string | undefined;
  description: string | undefined;
  aboutTitle: string | undefined;
  about: string | undefined;
  thumbnail: string | undefined;
  originalEpisodesLink: string | undefined;
  // aboutHub: string;
  // titleBulletList: string;
  authors: string | undefined;
  listeningIcon: string | undefined;
  readingIcon: string | undefined;
  listeningTitle: string | undefined;
  readingTitle: string | undefined;
  subscriptionText: string | undefined;
  firstChapterFreeListeningTitle: string | undefined;
  primaryColor: string | undefined;
  categories: string | undefined;
  subTitle: string | undefined;
  relatedContent: string | undefined;
  primaryColorLight: string | undefined;
  backgroundColor: string | undefined;
  backgroundColorLight: string | undefined;
}

export type BookMenuActions = {
  onShareClick: () => void;
};

interface BookSummaryProps extends Partial<ContentDetailsParameters> {
  title: string | undefined;
  author: string | undefined;
  durationMS: number | undefined;
  rate: number | undefined;
  progress: number | undefined;
  onListenClick: () => void;
  menu: BookMenuActions;
  isLocked: boolean | undefined;
  onReadClick?: () => void;
}

const BookSummary = ({
  title,
  author,
  authors,
  durationMS,
  rate,
  categories,
  thumbnail,
  readingTitle,
  readingIcon,
  listeningIcon,
  listeningTitle,
  aboutTitle,
  about,
  descriptionTitle,
  description,
  recommendedToTitle,
  recommendedTo,
  authorsTitle,
  onListenClick,
  onReadClick,
  progress,
  menu,
  isLocked,
}: BookSummaryProps) => {
  const { plan } = useCurrentPlan();

  return (
    <div className="p-5 dark:text-white">
      <div className="flex items-center justify-start px-8">
        <div className="flex w-[270px] flex-col items-center justify-center gap-y-2">
          {thumbnail ? (
            <img
              src={getPublicMediaUrl(thumbnail)}
              alt="book"
              width={270}
              height={256}
              className="rounded-xl"
            />
          ) : (
            <div className="h-[256px] w-[270px]"></div>
          )}
          <div className="flex w-full items-center justify-start gap-x-2">
            <ProgressBar value={progress || 0} height="h-1" />
            <span className="text-[14px] text-[#B5B5B5]">{progress}%</span>
          </div>
        </div>
        <div className="flex w-full flex-col items-start justify-center p-5">
          <div className="text-[30px] font-bold">{title}</div>
          <div className="my-3 space-y-2 text-[#727272]">
            <div className="text-[14px] font-bold">{author}</div>
            <div className="gap-x flex items-center justify-start text-[13px] font-medium">
              {categories}
            </div>
            <div className="gap-x mt-3 flex items-center justify-start">
              <Badge className="gap-x-2 bg-[#38A7B4]/10 hover:bg-[#38A7B4]/10 dark:bg-[#323232] dark:hover:bg-[#323232]">
                <Star size={15} className="fill-[#B8B8B8] text-[#B8B8B8]" />
                <div className="text-[#727272]">{rate?.toFixed(1)}</div>
              </Badge>
              <Dot />
              <div className="text-[14px] font-bold">
                {convertMsToHourMinSec(durationMS || 0)}
              </div>
            </div>
          </div>
          <div className="mt-6 flex w-full items-center justify-start gap-x-4">
            {isLocked === false ? (
              <>
                {readingTitle ? (
                  <ShadcnButton
                    onClick={() => {
                      onReadClick && onReadClick();
                    }}
                    className="border border-black bg-transparent text-black hover:bg-transparent hover:opacity-80 dark:border-white dark:bg-transparent dark:text-white dark:hover:bg-transparent"
                  >
                    <div className="flex items-center justify-center gap-x-2 px-5">
                      <SVG
                        src={getPublicMediaUrl(readingIcon)}
                        preProcessor={(code: string) =>
                          code
                            .replace(
                              /(fill=)"(?!none")[^"]+"/g,
                              'fill="currentColor"'
                            )
                            .replace(
                              /(stroke=)"(?!none")[^"]+"/g,
                              'stroke="currentColor"'
                            )
                        }
                        className="h-4 w-4"
                      />
                      <div>{readingTitle}</div>
                    </div>
                  </ShadcnButton>
                ) : null}
                <ShadcnButton
                  className="bg-primary hover:bg-primary/80"
                  onClick={onListenClick}
                >
                  <div className="flex items-center justify-center gap-x-2 px-5 text-white">
                    <SVG
                      src={getPublicMediaUrl(listeningIcon)}
                      preProcessor={(code: string) =>
                        code
                          .replace(
                            /(fill=)"(?!none")[^"]+"/g,
                            'fill="currentColor"'
                          )
                          .replace(
                            /(stroke=)"(?!none")[^"]+"/g,
                            'stroke="currentColor"'
                          )
                      }
                      className="h-4 w-4"
                    />
                    <div>{listeningTitle}</div>
                  </div>
                </ShadcnButton>
              </>
            ) : isLocked === true ? (
              <ShadcnButton
                className="bg-primary hover:bg-primary/80"
                action={() => {
                  setIsSubscriptionModalOpen(true);
                }}
              >
                <div className="flex items-center justify-center gap-x-2 px-5 text-white">
                  <TranslatedText text="content-details.buttons.subscribe to listen" />
                </div>
              </ShadcnButton>
            ) : null}
            <div className="ltr:ml-auto rtl:mr-auto">
              <BookMenu onShareClick={menu.onShareClick} />
            </div>
          </div>
        </div>
      </div>
      <div className="-mt-[90px] space-y-8 rounded-lg bg-[#F9F9F9] px-12 py-32 dark:bg-[#4F5056]">
        <div className="space-y-2">
          <div className="text-[20px] font-bold">{descriptionTitle}</div>
          <div className="text-[16px] font-bold">{description} </div>
        </div>
        <div className="space-y-2">
          <div className="text-[20px] font-bold">{aboutTitle}</div>
          <div className="text-[16px] font-bold">{about} </div>
        </div>
        <div className="space-y-2">
          <div className="text-[20px] font-bold">{authorsTitle}</div>
          <div className="text-[16px] font-bold">{authors} </div>
        </div>
        <div className="space-y-2">
          <div className="text-[20px] font-bold">{recommendedToTitle}</div>
          <MarkdownView
            markdown={formatRecommended(recommendedTo || "")}
            options={{ tables: true, emoji: true, breaks: true, lists: true }}
            className="text-[16px] font-bold"
          />
        </div>
      </div>
    </div>
  );
};

export default BookSummary;
